import { useState, useMemo, useEffect, useCallback } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { FaPlus, FaArrowsTurnRight } from 'react-icons/fa6';
import { FiEdit2, FiShield } from 'react-icons/fi';
import { IoIosRefresh } from 'react-icons/io';
import { toast } from 'react-toastify';
import ThreeColumnLayout from '../../layouts/ThreeColumnLayout';
import FilterSidebar from '../../organisms/FilterSidebar';
import FilterSkeleton from '../../atoms/FilterSkeleton';
import ATable from '../../organisms/ATable';
import TableSkeleton from '../../atoms/TableSkeleton';
import Loading from '../../ions/Loading';
import PageFooter from '../../molecules/PageFooter';
import TextEdit from '../../molecules/TextEdit';
import { collateralApi, ApiError } from '../../../services/collateralApi';
import NewAnalysisFromOperation from './NewAnalysisFromOperation';
import LoadReceivablesModal from './ReceivableModalForm';
import {
    formatCurrency,
    getGuaranteeStatusColor,
    getIntensityColor,
    getDocStatusColor,
    type GuaranteeStatus,
    type DocStatus,
} from './collateralUtils';

const getEnvironmentColor = (env: string): string => {
    if (env === 'prd') return 'text-red-600';
    if (env === 'hml') return 'text-yellow-600';
    return 'text-green-600';
};

const getDocBadgeLabel = (status: string): string => {
    if (status === 'OBRIGATORIO') return 'OBR';
    if (status === 'SIM, ANTES') return 'ANT';
    return status;
};

const DocBadge = ({ status, archived, pending }: { status: string; archived?: number; pending?: number }) => {
    if (!status || status === 'N/A' || status === '') return <span className="text-gray-300 text-xs">—</span>;
    const total = (archived ?? 0) + (pending ?? 0);
    return (
        <div className="flex flex-col items-center">
            <span className={`px-1 py-0.5 rounded text-[10px] font-medium leading-none ${getDocStatusColor(status as DocStatus)}`}>
                {getDocBadgeLabel(status)}
            </span>
            {total > 0 && <span className="text-[9px] text-gray-500 dark:text-gray-400 mt-0.5">{archived ?? 0}/{total}</span>}
        </div>
    );
};

const GuaranteePageList = () => {
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);
    const [statusFilter, setStatusFilter] = useState('');
    const [intensityFilter, setIntensityFilter] = useState('');
    const [analystFilter, setAnalystFilter] = useState('');
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [showNewForm, setShowNewForm] = useState(false);
    const [sortField, setSortField] = useState('');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');
    const [isLoading, setIsLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [rows, setRows] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loadReceivablesRow, setLoadReceivablesRow] = useState<any>(null);
    const [apiInfo, setApiInfo] = useState({ version: '', environment: '' });
    const pageSize = 50;

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const result = await collateralApi.getGuarantees({
                page, pageSize,
                status: statusFilter || undefined,
                intensity: intensityFilter || undefined,
                search: search || undefined,
            });
            setRows(result.items || []);
            setTotal(result.total || 0);
            setApiInfo({ version: result.apiVersion || '', environment: result.environment || '' });
        } catch (e: any) {
            if (e instanceof ApiError) {
                if (e.status === 403) return navigate('/denied');
                if (e.status === 404) return navigate('/not-found');
            }
            navigate('/error');
        } finally {
            setIsLoading(false);
            setInitialLoading(false);
        }
    }, [page, statusFilter, intensityFilter, search]);

    useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

    const statusCounts = useMemo(() => {
        const c: Record<string, number> = {};
        rows.forEach(r => { c[r.guaranteeStatus] = (c[r.guaranteeStatus] || 0) + 1; });
        return c;
    }, [rows]);

    const intensityCounts = useMemo(() => {
        const c: Record<string, number> = {};
        rows.forEach(r => { if (r.analysisIntensity) c[r.analysisIntensity] = (c[r.analysisIntensity] || 0) + 1; });
        return c;
    }, [rows]);

    const analystCounts = useMemo(() => {
        const c: Record<string, number> = {};
        rows.forEach(r => { if (r.trader) c[r.trader] = (c[r.trader] || 0) + 1; });
        return c;
    }, [rows]);

    const handleNewAnalysis = () => {
        setShowNewForm(false);
        setRefreshKey(k => k + 1);
        toast.success('Análise criada');
    };

    const clearFilters = () => { setStatusFilter(''); setIntensityFilter(''); setAnalystFilter(''); };

    const handleRowClick = (row: any) => {
        if (!row.totalTitles) return; // block navigation if no receivables
        navigate(`/collateral/guarantees/${row.id}/view`);
    };

    const filterGroups = initialLoading ? [] : [
        {
            title: '',
            children: (
                <div className="space-y-4">
                    <button onClick={clearFilters}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${!statusFilter && !intensityFilter && !analystFilter ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                        <div className="flex items-center justify-between">
                            <span>Todos</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">{total}</span>
                        </div>
                    </button>
                    <div className="border-t border-gray-200 dark:border-slate-700 pt-3">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-3">STATUS GARANTIA</div>
                        <div className="space-y-1">
                            {Object.entries(statusCounts).sort(([a], [b]) => a.localeCompare(b)).map(([status, count]) => (
                                <button key={status} onClick={() => { setStatusFilter(status); setIntensityFilter(''); setAnalystFilter(''); }}
                                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${statusFilter === status ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <span className={`w-2.5 h-2.5 rounded-full ${getGuaranteeStatusColor(status as GuaranteeStatus).split(' ')[0]}`} />
                                            <span>{status}</span>
                                        </div>
                                        <span className="text-xs text-gray-500 dark:text-gray-400">{count}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="border-t border-gray-200 dark:border-slate-700 pt-3">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-3">INTENSIDADE</div>
                        <div className="space-y-1">
                            {Object.entries(intensityCounts).sort(([a], [b]) => a.localeCompare(b)).map(([level, count]) => (
                                <button key={level} onClick={() => { setIntensityFilter(level); setStatusFilter(''); setAnalystFilter(''); }}
                                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${intensityFilter === level ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getIntensityColor(level as any)}`}>{level}</span>
                                        </div>
                                        <span className="text-xs text-gray-500 dark:text-gray-400">{count}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            ),
        },
        {
            title: 'ANALISTAS',
            children: (
                <>
                    {Object.entries(analystCounts).sort(([a], [b]) => a.localeCompare(b)).map(([name, count]) => (
                        <button key={name} onClick={() => { setAnalystFilter(name); setStatusFilter(''); setIntensityFilter(''); }}
                            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${analystFilter === name ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                            <div className="flex items-center justify-between">
                                <span>{name}</span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">{count}</span>
                            </div>
                        </button>
                    ))}
                </>
            ),
        },
    ];

    const headers = [
        { id: 'op', title: 'Operação', field: 'operationCode', canSort: true, width: '110px', cssClass: 'border-b' },
        { id: 'type', title: 'Tipo', field: 'operationType', canSort: true, width: '70px', cssClass: 'border-b text-center' },
        { id: 'assignor', title: 'Cedente', field: 'assignor', canSort: true, cssClass: 'border-b' },
        { id: 'obligation', title: 'Obrigação', field: 'obligation', canSort: true, width: '110px', cssClass: 'border-b text-center' },
        { id: 'intensity', title: 'Intensidade', field: 'analysisIntensity', canSort: true, width: '100px', cssClass: 'border-b text-center' },
        { id: 'titles', title: 'Títulos', field: 'totalTitles', canSort: true, width: '70px', cssClass: 'border-b text-right' },
        { id: 'amount', title: 'Valor Total', field: 'totalAmount', canSort: true, width: '130px', cssClass: 'border-b text-right' },
        { id: 'xml', title: 'XML', width: '55px', cssClass: 'border-b text-center', canSort: false },
        { id: 'nf', title: 'NF', width: '55px', cssClass: 'border-b text-center', canSort: false },
        { id: 'canh', title: 'Canh.', width: '55px', cssClass: 'border-b text-center', canSort: false },
        { id: 'dup', title: 'Dupl.', width: '55px', cssClass: 'border-b text-center', canSort: false },
        { id: 'statusG', title: 'Status', field: 'statusGuarantee', canSort: true, width: '130px', cssClass: 'border-b text-center' },
        { id: 'analyst', title: 'Analista', field: 'analystName', canSort: true, width: '90px', cssClass: 'border-b' },
        { id: 'actions', title: '', width: '40px', cssClass: 'border-b text-center', canSort: false },
    ];

    return (
        <>
            {isLoading && <Loading />}

            {!showNewForm && (
                <button onClick={() => setShowNewForm(true)}
                    className="fixed top-[69px] right-[10px] w-10 h-10 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 z-50 flex items-center justify-center border-2 border-white dark:border-slate-800 bg-blue-600 hover:bg-blue-700 text-white"
                    title="Nova Análise de Garantia">
                    <FaPlus size={14} />
                </button>
            )}

            <ThreeColumnLayout
                leftSidebarTitle={
                    <div className="flex items-center justify-between w-full">
                        <div className="flex items-center space-x-2">
                            <FiShield className="text-blue-600" size={18} />
                            <span>Garantias</span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">v1</div>
                    </div>
                }
                leftSidebar={
                    <FilterSidebar filterGroups={filterGroups}>
                        {initialLoading && <FilterSkeleton />}
                    </FilterSidebar>
                }
                showRightPanel={showNewForm}
                onCloseRightPanel={() => setShowNewForm(false)}
                rightPanel={showNewForm ? (
                    <div className="h-full p-6">
                        <NewAnalysisFromOperation onClose={() => setShowNewForm(false)} onCreated={handleNewAnalysis} />
                    </div>
                ) : null}
            >
                <div className="flex flex-col h-full bg-white dark:bg-slate-800 relative overflow-hidden">
                    {/* Header */}
                    <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-800 z-10">
                        <div className="flex items-center justify-between">
                            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Análise de Garantias</h1>
                            <div className="text-xs text-gray-500 dark:text-gray-400 ml-3">
                                {apiInfo.version && <div>v{apiInfo.version}</div>}
                                <div className={`font-medium ${getEnvironmentColor(apiInfo.environment)}`}>{(apiInfo.environment || '').toUpperCase()}</div>
                            </div>
                            <div className="flex items-center space-x-2">
                                <button onClick={() => setRefreshKey(k => k + 1)}
                                    className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                    title="Atualizar">
                                    <IoIosRefresh size={18} />
                                </button>
                                <div className="w-72">
                                    <TextEdit label="Buscar operação, cedente, analista..." value={searchInput}
                                        onChange={e => setSearchInput(e.target.value)}
                                        onKeyPress={e => { if (e.key === 'Enter') setSearch(searchInput); }} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="flex-1 overflow-auto [&_table]:table-fixed [&_table]:w-full">
                        {initialLoading ? (
                            <TableSkeleton rows={12} />
                        ) : (
                            <ATable headers={headers} fieldSorted={sortField} orderSorted={sortOrder}
                                onSort={(field: string, order: string) => { setSortField(field); setSortOrder(order as 'ASC' | 'DESC'); }}>
                                {rows.length === 0 ? (
                                    <tr><td colSpan={14} className="py-8 text-center text-gray-500 dark:text-gray-400">Nenhuma análise encontrada</td></tr>
                                ) : rows.map((row, index) => (
                                    <tr key={row.id}
                                        className={`group ${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-900'} hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer`}
                                        onClick={() => handleRowClick(row)}>
                                        <td className="py-2 px-3 text-sm font-medium dark:text-white">{row.operationCode}</td>
                                        <td className="py-2 px-3 text-center">
                                            <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 dark:bg-slate-600 dark:text-slate-200">{row.operationType}</span>
                                        </td>
                                        <td className="py-2 px-3 text-sm dark:text-white truncate" title={row.assignor}>{row.assignor}</td>
                                        <td className="py-2 px-3 text-center text-xs dark:text-gray-300">{row.obligation || '—'}</td>
                                        <td className="py-2 px-3 text-center">
                                            {row.analysisIntensity ? <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getIntensityColor(row.analysisIntensity)}`}>{row.analysisIntensity}</span> : '—'}
                                        </td>
                                        <td className="py-2 px-3 text-right text-sm dark:text-white">{row.totalTitles || '—'}</td>
                                        <td className="py-2 px-3 text-right text-sm dark:text-white">{formatCurrency(row.totalAmount)}</td>
                                        <td className="py-1 px-1"><DocBadge status={row.xml?.status} archived={row.xml?.archived} pending={row.xml?.pending} /></td>
                                        <td className="py-1 px-1"><DocBadge status={row.nfPdf?.status} archived={row.nfPdf?.archived} pending={row.nfPdf?.pending} /></td>
                                        <td className="py-1 px-1"><DocBadge status={row.canhoto?.status} archived={row.canhoto?.archived} pending={row.canhoto?.pending} /></td>
                                        <td className="py-1 px-1"><DocBadge status={row.duplicata?.status} archived={row.duplicata?.archived} pending={row.duplicata?.pending} /></td>
                                        <td className="py-2 px-3 text-center">
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getGuaranteeStatusColor(row.guaranteeStatus as GuaranteeStatus)}`}>{row.guaranteeStatus}</span>
                                        </td>
                                        <td className="py-2 px-3 text-sm dark:text-gray-300">{row.trader || '—'}</td>
                                        <td className="py-2 px-3 text-center">
                                            {!row.totalTitles ? (
                                                <button onClick={e => { e.stopPropagation(); setLoadReceivablesRow(row); }}
                                                    className="opacity-0 group-hover:opacity-100 text-orange-500 hover:text-orange-600 dark:text-orange-400 transition-opacity"
                                                    title="Carregar recebíveis">
                                                    <FaArrowsTurnRight size={14} />
                                                </button>
                                            ) : (
                                                <button onClick={e => { e.stopPropagation(); navigate(`/collateral/guarantees/${row.id}/edit`); }}
                                                    className="opacity-0 group-hover:opacity-100 text-blue-600 hover:text-blue-700 dark:text-blue-400 transition-opacity">
                                                    <FiEdit2 size={14} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </ATable>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex-shrink-0 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                        <PageFooter currentPage={page - 1} totalPages={Math.ceil(total / pageSize)} itemsPerPage={pageSize} totalElements={total} onPageChange={(p) => setPage(p + 1)} itemsLabel="análises" />
                    </div>
                </div>
            </ThreeColumnLayout>

            {loadReceivablesRow && (
                <LoadReceivablesModal
                    analysisId={loadReceivablesRow.id}
                    operationCode={loadReceivablesRow.operationCode}
                    offerOperationId={loadReceivablesRow.offerOperationId}
                    onClose={() => setLoadReceivablesRow(null)}
                    onSuccess={() => { setLoadReceivablesRow(null); setRefreshKey(k => k + 1); toast.success('Recebíveis carregados'); }}
                />
            )}

            <Outlet context={{ onDataChange: () => setRefreshKey(k => k + 1) }} />
        </>
    );
};

export default GuaranteePageList;
